/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { ClearOutlined, SearchOutlined } from "@ant-design/icons";
import ReportService from "../../../../service/ERP/Report.service";
import {
  saveAccessPageStateCookie,
  loadAccessPageStateCookie,
  clearAccessPageStateCookie,
} from "../../../../utils/accessPageStateCookie";
import {
  CATEGORY_ORDER,
  summaryColumns,
  allSummaryColumns,
  lotColumns,
} from "./model";
import StockMinAlertAccess from "../stock-min-alert/MyAccess";

const reportService = ReportService();
const PAGE_KEY = "erp_stock_summary";

export default function StockSummaryAccess() {
  const [form] = Form.useForm();

  const [activeSearch, setActiveSearch] = useState([]);
  const [activeTab, setActiveTab] = useState(CATEGORY_ORDER[0]);
  const [loading, setLoading] = useState(false);
  const [groupedData, setGroupedData] = useState({});

  const allItems = useMemo(() => {
    const keys = Object.keys(groupedData || {});
    return keys.reduce((acc, key) => acc.concat(groupedData[key] || []), []);
  }, [groupedData]);

  const totalItemCount = allItems.length;

  const saveToCookie = useCallback((searchValues) => {
    saveAccessPageStateCookie(PAGE_KEY, { searchValues });
  }, []);

  const handleSearch = useCallback(
    (forcedValues = null) => {
      const run = (values) => {
        saveToCookie(values);
        setLoading(true);
        reportService
          .getStockSummary(values || {})
          .then((res) => setGroupedData(res?.data?.data || {}))
          .catch(() => message.error("โหลดรายงานสต๊อกรวมไม่สำเร็จ"))
          .finally(() => setLoading(false));
      };

      if (forcedValues !== null) {
        run(forcedValues);
      } else {
        form.validateFields().then(run);
      }
    },
    [form, saveToCookie],
  );

  const handleClear = () => {
    clearAccessPageStateCookie(PAGE_KEY);
    form.resetFields();
    handleSearch({});
  };

  useEffect(() => {
    const restored = loadAccessPageStateCookie(PAGE_KEY);
    if (restored?.searchValues) {
      form.setFieldsValue(restored.searchValues);
    }
    handleSearch(restored?.searchValues ?? {});
  }, []);

  const lotCols = useMemo(() => lotColumns(), []);
  const itemCols = useMemo(() => summaryColumns(), []);
  const allCols = useMemo(() => allSummaryColumns(), []);

  const expandable = useMemo(
    () => ({
      expandedRowRender: (record) => (
        <Table
          size="small"
          rowKey="seq"
          columns={lotCols}
          dataSource={record.lots || []}
          pagination={false}
          scroll={{ x: "max-content" }}
          style={{ margin: "8px 16px" }}
          locale={{ emptyText: "ไม่มีข้อมูล Lot" }}
        />
      ),
      rowExpandable: (record) => (record.lots || []).length > 0,
    }),
    [lotCols],
  );

  const searchPanel = (
    <Collapse
      size="small"
      onChange={setActiveSearch}
      bordered={false}
      activeKey={activeSearch}
      items={[
        {
          key: "1",
          label: (
            <>
              <SearchOutlined />
              <span> ค้นหา</span>
            </>
          ),
          showArrow: false,
          children: (
            <>
              <br />
              <Form form={form} layout="vertical" autoComplete="off">
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="รหัสสินค้า" name="stcode">
                      <Input placeholder="กรอกรหัสสินค้า" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="ชื่อสินค้า" name="stname">
                      <Input placeholder="กรอกชื่อสินค้า" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Flex
                      align="flex-end"
                      justify="flex-end"
                      style={{ height: "100%" }}
                      gap={8}
                    >
                      <Button
                        type="primary"
                        size="small"
                        className="bn-action"
                        icon={<SearchOutlined />}
                        onClick={() => handleSearch()}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        className="bn-action"
                        danger
                        icon={<ClearOutlined />}
                        onClick={handleClear}
                      >
                        ล้าง
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </>
          ),
        },
      ]}
    />
  );

  /* ประเภทที่ระบุไม่ได้ ให้มีแท็บ "อื่นๆ" ด้วย ไม่งั้นยอดรวมจะไม่ตรงกับผลรวมแต่ละแท็บ */
  const tabCategories = [
    ...CATEGORY_ORDER,
    ...Object.keys(groupedData || {}).filter(
      (k) => !CATEGORY_ORDER.includes(k) && (groupedData[k] || []).length > 0
    ),
  ];

  const categoryTabs = tabCategories.map((cat) => {
    const data = groupedData?.[cat] || [];
    return {
      key: cat,
      label: (
        <Space size={6}>
          <span>{cat}</span>
          <Tag
            color={data.length > 0 ? "green" : "default"}
            style={{ margin: 0, borderRadius: 10 }}
          >
            {data.length}
          </Tag>
        </Space>
      ),
      children: (
        <Table
          size="small"
          rowKey="stcode"
          loading={loading}
          columns={itemCols}
          dataSource={data}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          expandable={expandable}
          locale={{ emptyText: "ไม่มีข้อมูลสต๊อก" }}
        />
      ),
    };
  });

  const allTab = {
    key: "all",
    label: (
      <Space size={6}>
        <span>รวมทั้งหมด</span>
        <Tag
          color={totalItemCount > 0 ? "blue" : "default"}
          style={{ margin: 0, borderRadius: 10 }}
        >
          {totalItemCount}
        </Tag>
      </Space>
    ),
    children: (
      <Table
        size="small"
        rowKey="stcode"
        loading={loading}
        columns={allCols}
        dataSource={allItems}
        scroll={{ x: "max-content" }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        expandable={expandable}
        locale={{ emptyText: "ไม่มีข้อมูลสต๊อก" }}
      />
    ),
  };

  const tabItems = [...categoryTabs, allTab];

  const summaryContent = (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      {searchPanel}
      <Card>
        <Flex
          justify="space-between"
          align="center"
          className="mb-3"
          wrap
          gap={8}
        >
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            เช็คสต๊อกรวม
          </Typography.Title>
          <Typography.Text type="secondary">
            {totalItemCount} รายการ
          </Typography.Text>
        </Flex>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          destroyInactiveTabPane={false}
        />
      </Card>
    </Space>
  );

  const items = [
    {
      key: "summary",
      label: "เช็คสต๊อกรวม",
      children: summaryContent,
    },
    {
      key: "min_alert",
      label: "รายงานสต๊อกขั้นต่ำ",
      children: <StockMinAlertAccess />,
    },
  ];

  return <Tabs items={items} destroyInactiveTabPane={false} />;
}
